import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, batch } from 'react-redux';
import Svg from '../shared/Svg';
import colors from '../../utils/colors';
import * as SalesforceRecordActions from '../../actions/salesforceRecords';
import * as RootActions from '../../actions/index';
import { getObject, postObjectRecord } from '../../../api/emailsApi/objects';
import { StatusBarAlert } from '../statusBar/StatusBarAlert';
import { DisposableAlert } from '../shared/DisposableAlert';
import { changeAlertText, clearAlertText } from '../../actions';
import { ShortText } from '../customFormFields/ShortText';
import { MediumText } from '../customFormFields/MediumText';
import { PicklistField } from '../customFormFields/Picklist';
import { LongText } from '../customFormFields/LongText';
import { DatePickerField } from '../customFormFields/DatePickerField';
import { Numbers } from '../customFormFields/Numbers';
import { LookupField } from '../customFormFields/Lookup';
import { Currency } from '../customFormFields/Currency';
import { Percent } from '../customFormFields/Percent';
import { BooleanField } from '../customFormFields/Boolean';
import { AssigneeLookupField } from '../customFormFields/AssigneeLookup';
import reactHtmlParser from 'react-html-parser';
import { setLocalStorageWithExpiry } from '../../utils/browser';


import './SalesforceRecordFormContainer.scss';
import { convertToText } from '../../utils/stringUtils';
/**
 * SalesforceRecordFormContainer component
 * @return {JSX.Element}
 */
const SalesforceRecordFormContainer = () => {
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.app.isLoading);
  const objectApiName = useSelector((state) => state.salesforceRecords.salesforceRecordObjectApiName);
  const cachedObjectFieldSets = useSelector((state) => state.salesforceRecords.cachedObjectFieldSets);
  const alertText = useSelector((state) => state.app.alertText);
  const user = useSelector((state) => state.auth.user);
  const { fieldSetApiName, objectLabel } = useSelector((state) => state.mail.settings.additionalObjectsAndFieldSets?.find((fieldSet) => fieldSet.objectApiName === objectApiName));
  const objectsAndDisplayFields = useSelector((state) => state.mail.settings.objectsAndDisplayFields);
  const selectedRecords = useSelector((state) => {
    return !state.app.isCalendar ?
      Object.values(state.mail.selectedRecords) :
      (state.calendar.selectedRecord ? // handle edge case where selectedRecord is null
        [state.calendar.selectedRecord] :
        []);
  });
  const [fields, setFields] = useState();
  const [formValues, setFormValues] = useState({});
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  useEffect(() => {
    if (isLoading && !isFormSubmitted) {
      handleGetObject();
    }
  }, [isLoading]);

  // make the api call to get the object
  const handleGetObject = async () => {
    // check if the object is already cached
    if (cachedObjectFieldSets[objectApiName]) {
      const cachedFields = cachedObjectFieldSets[objectApiName];

      if (cachedFields.find((field) => field.name === 'WhatId')) {
        // set default value to the first selected record
        cachedFields.find((field) => field.name === 'WhatId').default = selectedRecords?.[0]?.Id;
      }
      setFields(cachedFields);
      setFormValues(cachedFields.reduce((acc, field) => {
        acc[field.name] = field.default || null;
        return acc;
      }, {}));
      dispatch(RootActions.isLoading(false));
      return;
    }

    try {
      const response = await getObject(objectApiName, fieldSetApiName);
      // cache the object in redux
      dispatch(SalesforceRecordActions.setCachedObjectFieldSets({ [objectApiName]: response.data }));
      // cache the object in local storage for 4 hours
      setLocalStorageWithExpiry('cachedObjectFormFields', { ...cachedObjectFieldSets, [objectApiName]: response.data }, 14400000);
      const relatedToField = response.data.find((field) => field.name === 'WhatId');
      if (relatedToField) {
        // set default value to the first selected record
        relatedToField.default = selectedRecords?.[0]?.Id;
      }
      setFields(response.data);
      setFormValues(response.data.reduce((acc, field) => {
        acc[field.name] = field.default || null;
        return acc;
      }, {}));
    } catch (error) {
      console.error('Error getting object fields: ', error);
      dispatch(RootActions.changeAlertText('Error getting object fields', 'error'));
    }
    dispatch(RootActions.isLoading(false));
  };

  const onBackClicked = () => {
    dispatch(SalesforceRecordActions.setSalesforceRecordObjectApiName(null));
    dispatch(SalesforceRecordActions.setIsSalesforceRecordFormOpen(false));
  };

  // render fields based on the type
  const renderFields = () => {
    return fields.map((field) => {
      if (!field) { // edge case for field is null
        return null;
      } else if (field.name === 'OwnerId') { // handle OwnerId/AssignedTo
        return <AssigneeLookupField key={field.name} required={field.required} label={field.label} fieldName={reactHtmlParser(field.name)} handleChange={handleChange} error={field.error || ''} />;
      } else if (field.name === 'WhatId') { // handle WhatId/RelatedTo
        // set the Name field on the selected record using the objects
        const formattedSelectedRecords = Object.values(selectedRecords).map((record) => {
          const object = objectsAndDisplayFields?.find((object) => object.apiName === record.type);
          return { Name: convertToText(record[object.displayFields[0]?.apiName]) || record.Name, Id: record.Id };
        });
        return <LookupField key={field.name} enabled={field.enabled} required={field.required} label={field.label} fieldName={field.name} handleChange={handleChange} lookupOptions={formattedSelectedRecords || []} error={field.error || ''} defaultValue={field.default} disableIcon={false} lookup={field.lookup}/>;
      }
      switch (field.type) {
        case 'string':
          return field.length <= 18 ?
          <ShortText enabled={field.enabled} required={field.required} label={field.label} fieldName={field.name} handleChange={handleChange} length={field.length} error={field.error || ''} defaultValue={field.default} /> :
          <MediumText enabled={field.enabled} required={field.required} label={field.label} fieldName={field.name} handleChange={handleChange} length={field.length} error={field.error || ''} defaultValue={field.default} />;
        case 'picklist':
          return <PicklistField enabled={field.enabled} required={field.required} label={field.label} fieldName={field.name} handleChange={handleChange} values={field.values} error={field.error || ''} defaultValue={field.default} />;
        case 'textarea':
          return <LongText enabled={field.enabled} required={field.required} label={field.label} fieldName={field.name} handleChange={handleChange} length={field.length} error={field.error || ''} defaultValue={field.default}/>;
        case 'date':
          return <DatePickerField enabled={field.enabled} required={field.required} label={field.label} fieldName={field.name} handleChange={handleChange} error={field.error || ''} defaultValue={field.default}/>;
        case 'double':
        case 'int':
          return <Numbers enabled={field.enabled} required={field.required} label={field.label} fieldName={field.name} handleChange={handleChange} error={field.error || ''} defaultValue={field.default}/>;
        case 'lookup':
          return <LookupField enabled={field.enabled} required={field.required} label={field.label} fieldName={field.name} handleChange={handleChange} lookupOptions={field.value || []} error={field.error || ''} defaultValue={field.default} disableIcon={false} lookup={field.lookup}/>;
        case 'currency':
          return <Currency enabled={field.enabled} required={field.required} label={field.label} fieldName={field.name} handleChange={handleChange} error={field.error || ''} defaultValue={field.default}/>;
        case 'percent':
          return <Percent enabled={field.enabled} required={field.required} label={field.label} fieldName={field.name} handleChange={handleChange} error={field.error || ''} defaultValue={field.default}/>;
        case 'boolean':
          return <BooleanField enabled={field.enabled} required={field.required} label={field.label} fieldName={field.name} handleChange={handleChange} defaultValue={field.default} error={field.error || ''} />;
        case 'combobox':
          return <PicklistField enabled={field.enabled} required={field.required} label={field.label} fieldName={field.name} handleChange={handleChange} values={field.values} error={field.error || ''} defaultValue={field.default} isCombobox />;
        default:
          console.error('field type not found: ', field.type);
          return null;
      }
    });
  };

  // handle change for each field
  const handleChange = (e, apiName) => {
    // clear error for the field
    setFields(fields.map((field) => {
      if (field.name === e?.target?.name || field.name === apiName?.fieldName) {
        field.error = null;
      }
      return field;
    }));

    setFormValues((prev) => {
      // handle checkboxes
      if (e?.target?.type === 'checkbox') {
        prev[e?.target?.name || apiName?.fieldName] = e?.target?.checked || apiName?.value;
        return prev;
      }

      prev[e?.target?.name || apiName?.fieldName] = e?.target?.value || apiName?.value;
      return prev;
    });
  };

  const onCreateObjectRecord = async () => {
    const requiredFields = fields.filter((field) => field.required && !formValues[field.name]);
    if (requiredFields.length) {
      requiredFields.forEach((field) => {
        field.error = `${field.label} is required`;
      });
      setFields([...fields]);
      dispatch(changeAlertText(`Complete required fields to add ${objectLabel}.`, 'error'));
      throw new Error(`Complete required fields to add ${objectApiName}.`);
    }

    // Remove optional fields that were not set
    const filteredFormValues = Object.fromEntries(Object.entries(formValues).filter(
        ([key, value]) => value !== null && value !== undefined && value !== ''));

    // Create object record
    try {
      setIsFormSubmitted(true);
      dispatch(RootActions.isLoading(true));
      const postObjectRecordResponse = await postObjectRecord(objectApiName, filteredFormValues);
      const redirectLink = `${user.salesforceInstanceUrl}/lightning/r/${postObjectRecordResponse?.data?.id}/view`;
      batch(() => {
        dispatch(SalesforceRecordActions.setSalesforceRecordObjectApiName(null));
        dispatch(SalesforceRecordActions.setIsSalesforceRecordFormOpen(false));
        dispatch(SalesforceRecordActions.setSalesforceRecordRedirectLink(redirectLink));
        const recordAlertText = formValues?.Subject?.length > 15 ? `${objectLabel} '${formValues.Subject.slice(0, 15)}...' created` :
                                formValues?.Subject ? `${objectLabel} '${formValues.Subject}' created` :
                                `${objectLabel} created`;
        dispatch(changeAlertText((recordAlertText)));
        if (formValues?.Subject?.length > 15) {
          dispatch(SalesforceRecordActions.setSalesforceRecordTooltipContent(formValues.Subject));
        }
      });
    } catch (e) {
      console.error(`Error saving: ${objectApiName} to Salesforce`, e);
      batch(() => {
        dispatch(SalesforceRecordActions.setSalesforceRecordObjectApiName(null));
        dispatch(SalesforceRecordActions.setIsSalesforceRecordFormOpen(false));
        dispatch(changeAlertText(`Error saving ${objectLabel}`, 'error'));
      });
    }
    dispatch(RootActions.isLoading(false));
  };

  return (
    <div className={ isLoading ? 'salesforce-record-form-container-deactivated' : 'salesforce-record-form-container'}>
      { alertText &&
        <DisposableAlert
          child={<StatusBarAlert />}
          disposeFunction={() => {
            dispatch(clearAlertText());
          }}
          timeout={4000}
        />}
      <div className='salesforce-record-back-container'>
        <button // back button out of line on left side compared to mock
          className='salesforce-record-back'
          onClick={onBackClicked}
        >
          <Svg
            className='salesforce-record-back-icon'
            data='/assets/back.svg'
            fill={colors.strongBlue}
            onClick={onBackClicked}
          />
        Back
        </button>
      </div>
      <div className='salesforce-record-form-header'>
        <div className='salesforce-record-title'>{ objectLabel }</div>
        <div className='salesforce-record-description'>Add { objectLabel }</div>
      </div>
      <div className='salesforce-record-form'>
        {fields && renderFields()}
      </div>
      <div className='salesforce-record-button-container'>
        <button className='salesforce-record-button-cancel' onClick={onBackClicked}>Cancel</button>
        <button className='salesforce-record-button-submit' onClick={onCreateObjectRecord}>Create { objectLabel }</button>
      </div>
    </div>
  );
};

export default SalesforceRecordFormContainer;
